import { isDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';

import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorTileId,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput/index.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectTargetAccumulatorScheduleData,
  selectTargetAccumulatorScheduleFieldValidation,
  selectTargetAccumulatorScheduleTable,
} from '@/store/targetAccumulator/selectors.ts';
import type { AccumulatorScheduleLine } from '@/store/targetAccumulator/state.ts';

const Notional = ({ legId }: { legId: string }) => {
  const tileId = useTargetAccumulatorTileId();
  const notional = useAppSelector((state) => selectTargetAccumulatorScheduleTable(state, tileId, legId)?.amount);
  const isModified = useAppSelector(
    (state) =>
      selectTargetAccumulatorScheduleData(state, tileId).scheduleCustomFields?.[legId]?.includes('Amount') ?? false,
  );
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { validation } = useAppSelector((state) =>
    selectTargetAccumulatorScheduleFieldValidation(state, tileId, legId, 'amountString'),
  );

  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();

  const handleNotional = (value: number | undefined) => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Schedule notional changed',
      isDefined(value) ? String(value) : 'null',
    );

    return checkTargetAccumulatorWithTradeCapture({
      schedule: { [legId]: { amount: isDefined(value) ? value : null } as AccumulatorScheduleLine },
    });
  };

  return (
    <TradeCaptureValidationWrapper validation={validation}>
      <SgNumberInput
        valuePosition="start"
        selectAllOnFocus
        acceleratorsEnabled
        className={isModified ? 'text-info' : ''}
        value={isDefined(notional) ? notional : undefined}
        maxDecimals={9}
        onChange={handleNotional}
        size="sm"
        data-e2e="notional"
        data-e2e-is-modified={isModified}
      />
    </TradeCaptureValidationWrapper>
  );
};

export default Notional;
